/* eslint-disable max-len */
import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Snackbar, Typography } from '@mui/material';
import { SeelEmail, FullpageLoading, RadioPart } from '@components';
import CloseIcon from '@mui/icons-material/Close';
import { useEffectOnce } from '@hooks';
import useConnectionContext from '@hooks/useConnectionContext';
import { useNavigate } from 'react-router-dom';
import request from '@api';
import { getBPSettings } from '@api/getSettings';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import SingleHeader from '../../components/SingleHeader';
import classes from './index.module.css';

const SETTINGS = {
  title: 'Settings', subTitle: 'to make changes',
};
const TOAST_TEXT = {
  suc: 'Settings updated',
  notAuth: 'Not Authorized',
  err: 'Something went wrong, Please try again later',
};
const RADIOLIST = [
  {
    value: '1',
    label: 'Default On',
    recommended: 'Recommended',
    desc: 'Delivery Guarantee will be On by default. Buyers can choose to opt-out.',
  },
  {
    value: '2',
    label: 'Default Off',
    desc: 'Delivery Guarantee will be Off by default. Buyers can choose to opt-in.',
  },
];

const FULFILLMENT_REQUIREMENTS = [
  {
    value: 'fulfill_first_good',
    label: 'Fulfill when first item(s) are fulfilled',
    recommended: 'Recommended',
  },
  {
    value: 'fulfill_all_good',
    label: 'Fulfill when all items are fulfilled',
  },
  {
    value: 'fulfill_instant',
    label: 'Fulfill upon purchase',
  },
  {
    value: 'no_fulfill',
    label: 'Do not fulfill automatically',
  },
];
const fulfillmentText = ['Fulfill when first item(s) are fulfilled: Shipping Protection is fulfilled when any item(s) in this order are fulfilled.', 'Fulfill when all items are fulfilled: Shipping Protection is fulfilled when all other items in the order are fulfilled.', 'Fulfill upon purchase: Shipping Protection is fulfilled as soon as the order is placed.', 'Do not fulfill automatically: Shipping Protection is fulfilled when manual fulfillment marking.'];

export default function GSPSettings() {
  const [settingData, setSettingData] = useState({});
  const [radioValue, setRadioValue] = useState('1');
  const [fulfillmentValue, setFulfillmentValue] = useState('1');
  const [toast, setToast] = useState({ show: false, message: '' });
  const [pageLoading, setPageLoading] = useState(true);
  const navigate = useNavigate();
  const { store = {} } = useConnectionContext();
  const { info } = store;
  const [loading, setLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);

  const LightTooltip = styled(({ className, ...props }) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
  }));

  const getData = async () => {
    try {
      const { code, data = {} } = await getBPSettings();
      if (code === 0) {
        setSettingData(data);
        const { defaultOpt, fulfillmentSetting } = data || {};
        setRadioValue(defaultOpt ? '1' : '2');
        setFulfillmentValue(fulfillmentSetting);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { code } = await request('update-merchant-bp-settings', {
        method: 'POST',
        body: {
          defaultOn: radioValue === '1',
          fulfillmentSetting: fulfillmentValue,
        },
      });
      if (code === 0) {
        setToast({ show: true, message: TOAST_TEXT.suc });
        getData();
      }
    } catch (e) {
      console.log(e);
      const { originResponse: { status } = {} } = e;
      setToast({
        show: true,
        message: status === 401 ? TOAST_TEXT.notAuth : TOAST_TEXT.err,
      });
    }
    setLoading(false);
    setIsDisabled(true);
  };

  const handleToastClose = () => {
    setToast({ show: false });
  };
  const radioOnChange = (e, type) => {
    if (type === 1) {
      setFulfillmentValue(e.target.value);
      setIsDisabled((settingData.fulfillmentSetting === e.target.value));
    } else {
      setRadioValue(e.target.value);
      setIsDisabled((settingData.defaultOpt ? '1' : '2') === e.target.value);
    }
  };

  const getTextList = [
    {
      title: 'WIDGET SETTINGS',
      component: <RadioPart radioValue={radioValue} radioOnChange={radioOnChange} radioList={RADIOLIST} />,
    },
    {
      title: 'FULFILLMENT REQUIREMENTS',
      showTip: true,
      component: <RadioPart radioValue={fulfillmentValue} radioOnChange={(e) => radioOnChange(e, 1)} radioList={FULFILLMENT_REQUIREMENTS} />,
    },
  ];

  const handleClose = () => {
    navigate(-1);
  };
  useEffectOnce(async () => {
    setPageLoading(true);
    await getData();
    setPageLoading(false);
  }, []);

  if (!info || pageLoading) {
    return <FullpageLoading />;
  }
  const textList = ['shoppremiumoutlets.myshopify.com', 'sspotest.myshopify.com'].includes(info) ? getTextList.slice(0, 1) : getTextList;

  const tooltipPart = (
    <div className={classes.hover_container}>
      <Typography variant="body2" gutterBottom>
        {`This setting determines when Delivery Guarantee will be marked as “fulfilled” in your store. You have ${fulfillmentText.length} options:`}
      </Typography>

      {fulfillmentText.map((item) => (<div className={classes.hover_desc} key={item}>{item}</div>))}
    </div>
  );
  return (
    <div className={classes.settings}>
      <SingleHeader title="Delivery Guarantee Settings" handleClose={handleClose} />
      <div className={classes.content}>
        <div className={classes.title}>
          {SETTINGS.title}
        </div>
        <div className={classes.sub_title}>
          <SeelEmail text="Contact support" />
          {' '}
          {SETTINGS.subTitle}
        </div>
        <div className={classes.settings_content}>
          <div className={classes.panel}>
            Delivery Guarantee is
            <span className={classes.enabled}> enabled</span>
          </div>
          <div className={classes.panel}>
            {textList.map((setting) => (
              <div className={classes.setting} key={setting.title}>
                <div className={classes.setting_title}>
                  {setting.title}
                  {setting.showTip && (
                    <LightTooltip title={tooltipPart} placement="bottom-start">
                      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_16870_33356)">
                          <path d="M7.79175 4.95835H9.20842V6.37502H7.79175V4.95835ZM7.79175 7.79169H9.20842V12.0417H7.79175V7.79169ZM8.50008 1.41669C4.59008 1.41669 1.41675 4.59002 1.41675 8.50002C1.41675 12.41 4.59008 15.5834 8.50008 15.5834C12.4101 15.5834 15.5834 12.41 15.5834 8.50002C15.5834 4.59002 12.4101 1.41669 8.50008 1.41669ZM8.50008 14.1667C5.37633 14.1667 2.83341 11.6238 2.83341 8.50002C2.83341 5.37627 5.37633 2.83335 8.50008 2.83335C11.6238 2.83335 14.1667 5.37627 14.1667 8.50002C14.1667 11.6238 11.6238 14.1667 8.50008 14.1667Z" fill="black" />
                        </g>
                        <defs>
                          <clipPath id="clip0_16870_33356">
                            <rect width="17" height="17" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </LightTooltip>
                  )}
                </div>
                <div className={classes.setting_desc}>
                  {setting.component}
                </div>
              </div>
            ))}
          </div>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: '#645AFF',
              '&:hover': {
                backgroundColor: '#5950DF',
              },
            }}
            className={classes.button}
            disabled={isDisabled}
            loading={loading}
          >
            Save
          </LoadingButton>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            open={toast.show}
            onClose={handleToastClose}
            autoHideDuration={3000}
            message={toast.message}
            action={(
              <CloseIcon
                onClick={handleToastClose}
              />
            )}
            sx={{
              '& .MuiPaper-root': {
                height: '60PX',
                fontSize: '20PX',
              },

            }}
          />
        </div>
      </div>
    </div>
  );
}
